/** Live NPC interaction shell. Dialogue owns presentation; approved action handlers own
 * gameplay. The same paged text renderer and performance clock power the rehearsal. */
import { NPC_TIERS, canonicalArrivalLine, parseCanonicalNpcId,
  type NpcDef, type NpcRelMsg, type StoryStateV1, type StoryStateMsg } from "@hyperlight/shared";
import { npcDialogueNode, npcProfession, type NpcDialogueNode } from "../../../shared/src/social/npcInteraction";
import type { NpcShopMsg, NpcShopStateMsg } from "../../../shared/src/protocol";
import { ConversationDirector } from "../character/conversationDirector";
import { PERFORMANCE_LIBRARY } from "../character/conversationPatterns";
import { DialogueTextView } from "./dialogueText";
import { rememberConversationChoice } from "./conversationChoices";
import { audio } from "../audio";
import "./conversation.css";

export interface DialogQuestAction {
  op: "deliver" | "turnin"; q: string; npc: string; ob?: number; label: string; enabled: boolean;
}

export class DialogPanel {
  visible = false;
  private readonly el: HTMLElement;
  private readonly text: DialogueTextView;
  private readonly director: ConversationDirector;
  private readonly restoreChoice: () => void;
  private npc: NpcDef | null = null;
  private townName = "";
  private day = 0;
  private story: StoryStateV1 | null = null;
  private rel: NpcRelMsg | null = null;
  private node: NpcDialogueNode | null = null;
  private questActions: DialogQuestAction[] = [];
  private session = 0;
  private turn = 0;
  private raf = 0;
  private lastFrame = 0;
  private heldFor = 0;
  private choicesKey = "";
  private announced = "";
  private voice = true;
  private shopOpen = false;
  private shopPending = false;
  private shopElapsed = 0;
  private shopSeq = 0;
  private shopState: NpcShopStateMsg | null = null;

  constructor(private readonly handlers: {
    onTalk: (npcId: string) => void; onClose: () => void;
    onHold?: (npcId: string) => void;
    onQuestAction?: (a: DialogQuestAction) => void;
    onType?: (c: string) => void; onTypeEnd?: () => void;
    onShop?: (request: NpcShopMsg) => boolean;
  }) {
    this.el = document.createElement("section"); this.el.id = "npc-dialog";
    this.el.className = "conversation-ui";
    this.el.setAttribute("aria-label", "NPC conversation");
    this.el.style.display = "none";
    this.el.innerHTML = `
      <div id="npc-prose" class="conversation-bubble">
        <span id="npc-name" class="conversation-name"></span>
        <div id="npc-line" class="conversation-line"></div>
        <div class="conversation-progress"><span id="npc-page"></span><button id="npc-next" class="conversation-hint">▾ Continue · F</button></div>
      </div>
      <div id="npc-options" class="conversation-choices" aria-label="Responses"></div>
      <div id="npc-shop" class="conversation-service" hidden></div>
      <div class="conversation-tools"><button id="npc-voice" class="conversation-hint" aria-pressed="true">Voice on</button><button id="npc-leave" class="conversation-hint" aria-label="End conversation">Leave · Esc</button></div>
      <div class="conversation-sr"><span id="npc-role"></span><span id="npc-tier"></span><span id="npc-flavor"></span></div>
      <div id="npc-accessible" class="conversation-sr" aria-live="polite"></div>`;
    document.body.appendChild(this.el);
    this.restoreChoice = rememberConversationChoice(this.q("#npc-options"));
    this.text = new DialogueTextView(this.q("#npc-line"), ends => this.director.setPageEnds(ends));
    this.director = new ConversationDirector({ apply: () => {}, mouth: (char, emphasis) => {
      if (char) this.handlers.onType?.(char); else this.handlers.onTypeEnd?.();
      if (this.voice && this.npc) audio.dialogue(this.npc.id, this.npc.traits, char, emphasis, this.npc.style.body);
    } }, (text, spans) => this.text.setContent(text, spans));
    this.q("#npc-next").onclick = () => this.advancePage();
    this.q("#npc-line").onclick = () => this.advancePage();
    this.q("#npc-leave").onclick = () => this.close();
    this.q("#npc-voice").onclick = () => {
      this.voice = !this.voice; audio.stopDialogue();
      this.q("#npc-voice").textContent = this.voice ? "Voice on" : "Voice off";
      this.q("#npc-voice").setAttribute("aria-pressed", String(this.voice));
    };
    window.addEventListener("keydown", e => {
      if (!this.visible || !["KeyF", "Space", "Escape"].includes(e.code) || e.repeat) return;
      if ((e.target as HTMLElement)?.matches("input,textarea,select,[contenteditable=true]")) return;
      if (e.code === "Space" && (e.target as HTMLElement)?.closest("button")) return;
      e.preventDefault(); e.stopImmediatePropagation();
      if (e.code === "Escape") { if (this.shopOpen) this.closeShop(); else this.close(); }
      else this.advancePage();
    }, true);
    document.addEventListener("visibilitychange", () => {
      audio.stopDialogue(); this.handlers.onTypeEnd?.(); this.lastFrame = performance.now();
    });
  }
  private q(id: string): HTMLElement { return this.el.querySelector(id)!; }
  private button(label: string, run: () => void): HTMLButtonElement {
    const button = document.createElement("button"); button.textContent = label; button.onclick = run; return button;
  }
  private advancePage(): void {
    if (this.shopOpen) return;
    if (this.director.snapshot.awaitingPage) this.director.nextPage(); else this.director.skip();
    audio.stopDialogue(); this.render();
  }
  private showNode(id: string): void {
    if (!this.npc) return;
    const canonical = parseCanonicalNpcId(this.npc.id);
    const greeting = canonical && this.story ? canonicalArrivalLine(canonical.slug, this.story) : null;
    this.node = npcDialogueNode(this.npc, id, { townName: this.townName, day: this.day,
      tier: this.rel?.tier, salt: this.rel?.salt, greeting });
    const turnId = `turn-${++this.turn}`;
    this.director.prepare(turnId);
    this.director.commit({ version: 1, sceneId: `npc-${this.session}`, turnId, libraryVersion: PERFORMANCE_LIBRARY,
      seed: 17, segments: [{ id: turnId, text: this.node.text, spans: this.node.spans, deliveryId: "measured_welcome" }], resume: "listen" });
    this.choicesKey = ""; this.announced = ""; this.render();
  }
  private render(): void {
    const s = this.director.snapshot, ready = s.phase === "listening";
    this.text.render(s.text.length, s.pageIndex);
    const next = this.q("#npc-next") as HTMLButtonElement;
    next.hidden = this.shopOpen || ready; next.disabled = s.phase !== "performing";
    next.textContent = s.awaitingPage ? "▾ Continue · F" : "Reveal · F";
    this.q("#npc-page").textContent = !this.shopOpen && s.fullText ? `${s.pageIndex + 1} / ${s.pageCount}` : "";
    if (!this.shopOpen && (s.awaitingPage || ready) && this.announced !== `${this.turn}:${s.pageIndex}`) {
      this.announced = `${this.turn}:${s.pageIndex}`; this.q("#npc-accessible").textContent = this.text.pageText;
    }
    const key = `${this.node?.id}:${ready}:${this.shopOpen}`;
    if (key === this.choicesKey) return;
    this.choicesKey = key;
    const options = this.q("#npc-options"); options.replaceChildren();
    options.hidden = this.shopOpen;
    if (!ready || !this.node || this.shopOpen) return;
    for (const choice of this.node.choices) {
      const button = this.button(choice.label, () => {
        if (choice.kind === "dialogue") {
          if (choice.node === "chat" && this.npc) this.handlers.onTalk(this.npc.id);
          this.showNode(choice.node);
        } else if (choice.kind === "panel") this.openShop();
        else this.close();
      });
      button.dataset.choice = choice.id; if (choice.id === "chat") button.id = "npc-talk"; options.appendChild(button);
    }
    if (this.node.id === "root") for (const action of this.questActions) {
      const button = this.button(action.label, () => this.handlers.onQuestAction?.(action));
      button.disabled = !action.enabled; button.dataset.qop = action.op; options.appendChild(button);
    }
    this.restoreChoice();
    if (document.activeElement === next) options.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
  }
  private frame = (now: number): void => {
    if (!this.visible) return;
    const dt = Math.min(0.1, Math.max(0, (now - this.lastFrame) / 1000)); this.lastFrame = now;
    if (!document.hidden) this.director.advance(dt);
    if (this.npc && !document.hidden && (this.heldFor += dt) >= 20) {
      this.heldFor = 0; this.handlers.onHold?.(this.npc.id);
    }
    if (this.shopPending && (this.shopElapsed += dt) > 8) {
      this.shopPending = false; this.shopSeq++;
      this.renderShop("No reply. Check your inventory before trying a purchase again.");
    }
    this.render(); this.raf = requestAnimationFrame(this.frame);
  };
  // The conversation uses the world actors; keep the host API without a second render.
  portraitContext(): CanvasRenderingContext2D | null { return null; }
  setPortraitActive(_on: boolean): void {}
  open(npc: NpcDef, townName: string, dayIndex: number, story: StoryStateV1 | null = null): void {
    cancelAnimationFrame(this.raf); this.director.cancel(true); audio.stopDialogue();
    this.npc = npc; this.townName = townName; this.day = dayIndex; this.story = story; this.rel = null;
    this.session++; this.shopSeq++; this.shopOpen = this.shopPending = false; this.shopState = null;
    this.visible = true; this.el.style.display = "block"; this.q("#npc-prose").hidden = false; this.q("#npc-shop").hidden = true;
    this.q("#npc-name").textContent = npc.name; this.q("#npc-role").textContent = npcProfession(npc);
    this.q("#npc-tier").textContent = ""; this.q("#npc-flavor").textContent = ""; this.questActions = [];
    this.director.begin(`npc-${this.session}`); this.showNode("root");
    this.heldFor = 0; this.lastFrame = performance.now(); this.raf = requestAnimationFrame(this.frame);
    void audio.unlock(); this.q("#npc-next").focus({ preventScroll: true });
  }
  applyRel(msg: NpcRelMsg): void {
    if (!this.visible || msg.npc !== this.npc?.id) return;
    this.rel = msg;
    this.q("#npc-tier").textContent = `· ${NPC_TIERS[msg.tier] ?? NPC_TIERS[0]}`;
    this.q("#npc-flavor").textContent = msg.tier >= 1 ? this.npc.traits.join(" · ") : "";
    // Relationship echoes update context, never replace a sentence being read.
  }
  applyStory(msg: StoryStateMsg): void { this.story = msg.state; }
  currentNpcId(): string | null { return this.npc?.id ?? null; }
  setQuestActions(actions: DialogQuestAction[]): void {
    this.questActions = actions; this.choicesKey = ""; if (this.visible) this.render();
  }
  private openShop(): void {
    this.shopOpen = true; audio.stopDialogue(); this.handlers.onTypeEnd?.();
    this.q("#npc-prose").hidden = true; this.q("#npc-shop").hidden = false;
    this.render(); this.requestShop("list");
  }
  private closeShop(): void {
    this.shopOpen = this.shopPending = false; this.shopSeq++;
    this.q("#npc-prose").hidden = false; this.q("#npc-shop").hidden = true; this.render();
  }
  private requestShop(op: "list" | "buy", offer?: string): void {
    if (!this.npc || this.shopPending) return;
    this.shopPending = true; this.shopElapsed = 0;
    const sent = this.handlers.onShop?.({ t: "npcShop", npc: this.npc.id, seq: ++this.shopSeq, op, offer });
    if (!sent) this.shopPending = false;
    this.renderShop(sent ? undefined : "Connect to the server to browse or buy.");
  }
  applyShop(msg: NpcShopStateMsg): void {
    if (!this.visible || !this.shopOpen || !this.shopPending || msg.npc !== this.npc?.id || msg.seq !== this.shopSeq) return;
    this.shopPending = false; this.shopState = msg; this.renderShop(msg.err ?? (msg.bought ? "Purchased. Your inventory has been updated." : undefined));
  }
  private renderShop(message?: string): void {
    const panel = this.q("#npc-shop"); panel.replaceChildren();
    const title = document.createElement("p");
    title.textContent = this.shopState ? `Available goods · ${this.shopState.credits} credits` : "Available goods";
    panel.appendChild(title);
    for (const offer of this.shopState?.offers ?? []) {
      const row = document.createElement("div"); row.className = "conversation-shop-row";
      const label = document.createElement("span"); label.textContent = `${offer.name} · ${offer.price} cr`;
      const buy = this.button("Buy one", () => this.requestShop("buy", offer.id));
      buy.disabled = this.shopPending || (this.shopState?.credits ?? 0) < offer.price;
      buy.dataset.offer = offer.id; row.append(label, buy); panel.appendChild(row);
    }
    const status = document.createElement("p"); status.setAttribute("role", "status");
    status.style.fontSize = "11px"; status.textContent = message ?? (this.shopPending ? "Waiting for the shop…" : ""); panel.appendChild(status);
    if (!this.shopPending && message) panel.appendChild(this.button("Refresh stock", () => this.requestShop("list")));
    panel.appendChild(this.button("Back to conversation", () => this.closeShop()));
  }
  close(): void {
    if (!this.visible) return;
    cancelAnimationFrame(this.raf); this.director.cancel(true); audio.stopDialogue(); this.handlers.onTypeEnd?.();
    this.shopSeq++; this.shopPending = this.shopOpen = false; this.visible = false; this.npc = null; this.story = null;
    this.questActions = []; this.el.style.display = "none"; this.handlers.onClose();
  }
}
